import { AnnaErrorBase } from "@anna-money/anna-web-lib/utils";
import { isWebticsError, setupWebtics, webticsCall } from "@anna-money/webtics";
import { reaction } from "mobx";
import { type UserStore } from "services/user/userStore";
import {
  areCookiesDisabled,
  areThirdPartyScriptsBlocked,
  isAdBlockerDetected,
  isDNTEnabled,
  isLocalStorageBlocked,
  isSessionStorageBlocked,
} from "./privacyDetectionUtils";

export interface IAnalyticsManager {
  event: (eventType: string, payload?: object) => void;
  googleEvent: (eventType: string) => void;
}

class WebticsError extends AnnaErrorBase {
  constructor(message: string, cause: unknown) {
    super(message, { cause });
    this.name = "WebticsError";
  }
}

export class AnalyticsManager implements IAnalyticsManager {
  constructor(
    apiUrl: string,
    webticsTrackerUrl: string,
    private readonly _appVersion: string,
  ) {
    setupWebtics(
      webticsTrackerUrl,
      { apiUrl, appName: "formation", consentMode: "forced" },
      {
        errorHandler: (e) => {
          if (!isWebticsError(e, "network")) {
            console.error(new WebticsError("Webtics error", e));
          }
        },
      },
    );
    webticsCall("setContext", { app_version: _appVersion });
  }

  configure(userStore: UserStore): void {
    reaction(
      () => userStore.user,
      () => {
        webticsCall("setAlias", userStore.user.alias);
      },
      { fireImmediately: true },
    );

    // Run privacy detection after scripts have time to load
    setTimeout(() => {
      this._detectAndLogPrivacyIssues();
    }, 2000);
  }

  event(eventType: string, payload?: object): void {
    this._logAnnaEvent(eventType, payload);
  }

  experimentEvent(experimentName: string, toggle: boolean): void {
    this.event("experiment", {
      name: experimentName,
      variant: toggle ? "Treatment" : "Control",
    });
  }

  googleEvent(eventType: string): void {
    window.gtag("event", eventType);
  }

  private _logAnnaEvent(eventType: string, payload?: object): void {
    const effectivePayload = {
      ...payload,
      app_version: this._appVersion,
    };
    webticsCall("event", eventType, effectivePayload);
  }

  private _detectAndLogPrivacyIssues(): void {
    try {
      // Detect all privacy issues and log as a single event
      const privacyStatus = {
        adBlocked: isAdBlockerDetected(),
        cookiesDisabled: areCookiesDisabled(),
        localStorageBlocked: isLocalStorageBlocked(),
        sessionStorageBlocked: isSessionStorageBlocked(),
        thirdPartyScriptsBlocked: areThirdPartyScriptsBlocked(),
        doNotTrackEnabled: isDNTEnabled(),
      };

      // Only log if at least one issue is detected
      const hasAnyIssue = Object.values(privacyStatus).some((value) => value);

      if (hasAnyIssue) {
        this.event("browser.status-disabled", privacyStatus);
      }
    } catch (error) {
      // Silently fail - don't break the app if detection has issues
      console.error("Privacy detection error:", error);
    }
  }
}
